import Dexie from 'dexie';
import { toUpper } from 'lodash';

import {
  DB_SCHEMAS,
  IS_PRODUCTION,
  DB_NAME_PREFIX,
  DB_TABLE_NAMES,
} from 'Common/Constants';

/** Object of Table Names: { AccessToken: 'AccessToken', TransactionHistory: 'TransactionHistory', ... } */
export const DB_TABLE_NAMES_MAP = DB_TABLE_NAMES.reduce(
  (res, key) => ({ ...res, [key]: key }),
  {}
);

/** Returns MPW Offline databases */
export async function getDatabaseNames() {
  const databases = await Dexie.getDatabaseNames();
  return databases.filter(database => database.includes(DB_NAME_PREFIX));
}

/**
 * @param {String} district
 */
export async function getDistrictDatabaseNames(district) {
  if (!district) {
    return [];
  }
  const allDatabase = await getDatabaseNames();
  return allDatabase.filter(database =>
    database.split('_').includes(`${district}`)
  );
}

/**
 * Generates unique database identifier per user, per district.
 */
export function generateIdentifier(district, username) {
  return toUpper(`${district}_${username}`);
}

/**
 * Generates the connection string to the dexie database based on identifier.
 * The V7 is a remanent of the old versioning system which isn't easily removed without data loss as it's difficult to rename a Dexie database.
 */
export function generateDatabaseName(identifier) {
  return `${DB_NAME_PREFIX}_V7_${identifier}`;
}

/** DEVELOPMENT USAGE ONLY */
export async function deleteOlderVersions() {
  if (IS_PRODUCTION) {
    return;
  }

  const dbs = await getDatabaseNames();
  const olderDbs = dbs.filter(
    database => !database.includes(`${DB_NAME_PREFIX}_V7_`)
  );
  for (const olderDb of olderDbs) {
    const db = openDb(olderDb);
    await db
      .delete()
      .then(() => console.log(`Deleted ${olderDb}`))
      .catch(() => console.error(`Unable to delete ${olderDb}`));
  }
}

function subScribeToEvents(db) {
  db.on('versionchange', function(event) {
    if (
      window.confirm(
        `Your offline database was updated to version ${event.newVersion}. Refresh this page to get in sync now or cancel if you plan to refresh later.`
      )
    ) {
      // Refresh current webapp so that it starts working with newer DB schema.
      window.location.reload();
    } else {
      // Will let user finish its work in this window and
      // block the other window from upgrading.
      return false;
    }
  });
}

/** DB Connections */
const DBS = {};

function openDb(name) {
  const db = new Dexie(name);

  DB_SCHEMAS.forEach(x => {
    db.version(x.version).stores(x.schema);
  });

  subScribeToEvents(db);
  db.open();
  DBS[name] = db;
  return db;
}

/** Checks if the database is open */
function isDbOpen(name) {
  if (!DBS[name]) {
    return false;
  }
  return DBS[name].isOpen();
}

export function closeDb(name) {
  if (isDbOpen(name)) {
    DBS[name].close();
    delete DBS[name];
  }
}

/**
 * Opens a database connection to indexedb and returns the connection
 * or returns the connection if already open
 */
export async function getDB(name) {
  var db;

  if (!isDbOpen(name)) {
    db = openDb(name);
  }

  db = DBS[name];
  return db;
}

export default async name => getDB(name);



// WEBPACK FOOTER //
// ./src/common/DB.js